import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Stack,
    IconButton,
    Button,
    Divider,
    Chip,
    Toolbar,
    MenuItem,
} from '@mui/material';
import { AddCircleOutline, CurrencyRupeeSharp, KeyboardArrowLeft, RemoveCircleOutline } from '@mui/icons-material';
import { API } from '../../../utils/Api';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const PettyCash = () => {
    const navigate = useNavigate();
    const [remark, setRemark] = useState('');
    const [items, setItems] = useState([
        { id: 1, name: '', quantity: 1, amount: 0, unit: '' },
    ]);
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [manualAmount, setManualAmount] = useState('');
    const token = localStorage.getItem('token');
    const storedOutletData = localStorage.getItem('outletData');
    const outletData = JSON.parse(storedOutletData || '{}');
    const UNIT_OPTIONS = [
        'LITRES',
        'GRAMS',
        'KILOGRAMS',
        'PACKETS',
        'PCS',
        'BOX',
        'NOS',
        'MM',
        'ML',
    ];

    const handleItemChange = (id, field, value) => {
        setItems(prev =>
            prev.map(item =>
                item.id === id
                    ? {
                        ...item,
                        [field]:
                            field === 'quantity' || field === 'amount'
                                ? Number(value) || 0
                                : value,
                    }
                    : item
            )
        );
    };

    const handleAddItem = () => {
        setItems(prev => [
            ...prev,
            { id: Date.now(), name: '', quantity: 1, amount: 0, unit: '' },
        ]);
    };

    const handleRemoveItem = id => {
        setItems(prev => (prev.length === 1 ? prev : prev.filter(i => i.id !== id)));
    };

    const handleFileChange = e => {
        const file = e.target.files?.[0] || null;
        setInvoiceFile(file);
    };

    const itemsTotal = items.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0
    );

    const totalAmount = manualAmount
        ? Number(manualAmount) || 0
        : itemsTotal;

    const remainingBalance = (Number(currentBalance) || 0) - totalAmount;

    const formatAmount = value =>
        (Number(value) || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const handleSubmit = async e => {
        e.preventDefault();
        if (!invoiceFile) {
            toast.error('Please upload invoice before saving.', {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }

        try {
            const formData = new FormData();
            formData.append('outlet', outletData?._id);
            formData.append('amount', totalAmount);
            formData.append('remark', remark);
            const filledItems = items.filter(
                item => item.name.trim() || Number(item.amount) > 0
            );
            filledItems.forEach((item, index) => {
                formData.append(`items[${index}][itemName]`, item.name);
                formData.append(`items[${index}][quantity]`, item.quantity);
                formData.append(`items[${index}][amount]`, item.amount);
                if (item.unit) {
                    formData.append(`items[${index}][unit]`, item.unit);
                }
            });
            if (invoiceFile) {
                formData.append('invoice', invoiceFile);
            }
            const requestUrl = `${API.BASE_URL}outlet/petty-cash/debit`;
            const response = await axios.post(requestUrl, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            toast.success("Petty Cash transaction added successfully");
            handleReset();
            getPettyCashAmount();
            navigate('/petty-cash-transactions');
        } catch (error) {
            console.error('Error submitting petty cash:', error, error?.response?.data?.data?.message || error?.response?.data?.error);
            toast.error(
                error?.response?.data?.data?.message || error?.response?.data?.error, 
                {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                }
            );
        }
    };

    const handleReset = () => {
        setRemark('');
        setItems([{ id: 1, name: '', quantity: 1, amount: 0 }]);
        setInvoiceFile(null);
        setManualAmount('');
    };

    const isSaveDisabled =
        !remark.trim() ||
        totalAmount <= 0;

    const getPettyCashAmount = async () => {
        try {
            setLoading(true);
            const requestUrl = `${API.BASE_URL}outlet/petty-cash/cash-balance?outlet=${outletData?._id}`;
            const response = await axios.get(requestUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const balance =
                response?.data?.data?.outletPettyCashCurrentBalance?.cash_balance;
            setCurrentBalance(Number(balance) || 0);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPettyCashAmount();
    }, []);

    return (
        <Box sx={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            <Toolbar />
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    maxWidth: 900,
                    mx: 'auto',
                    mt: 8,
                    px: 3,
                    pb: 4,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KeyboardArrowLeft onClick={() => navigate(-1)} fontSize='large' sx={{ cursor: 'pointer' }} />
                    <Box
                        sx={(theme) => ({
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.primary.light + '22',
                            color: theme.palette.primary.main,
                        })}
                    >
                        <CurrencyRupeeSharp fontSize="medium" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                        <Typography variant="h5" fontWeight={700} color="#003049">
                            Petty Cash Entry
                        </Typography>
                    </Box>
                </Box>
                {/* <Box sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <KeyboardArrowLeft onClick={() => navigate(-1)} fontSize='large' sx={{ cursor: 'pointer' }} />
                    <Typography variant="h5" fontWeight={700} color="#003049">
                        Petty Cash Entry
                    </Typography>
                </Box> */}
                <Box sx={{ mb: 2.5 }}>
                    <Paper
                        elevation={2}
                        sx={{
                            borderRadius: 3,
                            px: 2.5,
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'linear-gradient(135deg, #003049, #045182)',
                            color: '#fff',
                        }}
                    >
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.8,
                                    opacity: 0.9,
                                }}
                            >
                                Current Petty Cash Balance
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                ₹ {formatAmount(currentBalance)}
                            </Typography>
                        </Box>
                        <Chip
                            label="Available"
                            size="small"
                            sx={{
                                color: '#003049',
                                backgroundColor: '#ffffff',
                                fontWeight: 600,
                            }}
                        />
                    </Paper>
                </Box>

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 3,
                        p: { xs: 2.5, md: 3 },
                        overflow: 'hidden',
                    }}
                >
                    {/* Remark */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            Remark / Description
                        </Typography>
                        <TextField
                            placeholder="Eg. Office tea & snacks, Xerox bills, stationery, etc."
                            value={remark}
                            onChange={e => setRemark(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                        />
                    </Box>

                    {/* Direct Amount Input */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            Amount
                        </Typography>
                        <TextField
                            type="number"
                            placeholder="Enter total amount"
                            value={manualAmount}
                            onChange={e => setManualAmount(e.target.value)}
                            fullWidth
                            onWheel={e => e.target.blur()}
                            inputProps={{
                                min: 0,
                                step: '0.01',
                                style: { textAlign: 'right' },
                            }}
                            sx={{
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                            }}
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block' }}
                        >
                            You can either enter total amount here or provide item-wise
                            details below.
                        </Typography>
                    </Box>

                    {/* Items header */}
                    <Box
                        sx={{
                            mb: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            Items (optional)
                        </Typography>
                        <Chip
                            size="small"
                            label={`${items.length} item${items.length > 1 ? 's' : ''}`}
                            sx={{ fontSize: 11, height: 22 }}
                        />
                    </Box>

                    {/* Items list */}
                    <Stack spacing={1.2}>
                        {items.map((item, index) => {
                            return (
                                <Paper
                                    key={item.id}
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#fafbfc',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 1,
                                        '&:hover': {
                                            boxShadow: 1,
                                            borderColor: '#c5c9cc',
                                        },
                                    }}
                                >
                                    <TextField
                                        label="Item Name"
                                        placeholder="Eg. Tea, Pen, Xerox"
                                        value={item.name}
                                        onChange={e =>
                                            handleItemChange(item.id, 'name', e.target.value)
                                        }
                                        size="small"
                                        sx={{
                                            flex: {
                                                xs: '1 1 100%',
                                                sm: '2 1 230px',
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Qty"
                                        type="number"
                                        value={item.quantity}
                                        onWheel={e => e.target.blur()}
                                        onChange={e =>
                                            handleItemChange(item.id, 'quantity', e.target.value)
                                        }
                                        size="small"
                                        sx={{
                                            width: 90,
                                            '& .MuiOutlinedInput-input': {
                                                textAlign: 'right',
                                                fontWeight: 600,
                                            },
                                            '& input[type=number]': {
                                                MozAppearance: 'textfield',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0,
                                            },
                                        }}
                                        inputProps={{ min: 0 }}
                                    />

                                    <TextField
                                        label="Unit (optional)"
                                        select
                                        value={item.unit || ''}
                                        onChange={e =>
                                            handleItemChange(item.id, 'unit', e.target.value)
                                        }
                                        size="small"
                                        sx={{ width: 160 }}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {UNIT_OPTIONS.map(unit => (
                                            <MenuItem key={unit} value={unit}>
                                                {unit}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        label="Amount"
                                        type="number"
                                        value={item.amount}
                                        onChange={e =>
                                            handleItemChange(item.id, 'amount', e.target.value)
                                        }
                                        onWheel={e => e.target.blur()}
                                        size="small"
                                        sx={{
                                            width: 150,
                                            '& .MuiOutlinedInput-input': {
                                                textAlign: 'right',
                                                fontWeight: 600,
                                            },
                                            '& input[type=number]': {
                                                MozAppearance: 'textfield',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0,
                                            },
                                        }}
                                        inputProps={{
                                            min: 0,
                                            step: '0.01',
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            ml: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <IconButton
                                            color="error"
                                            onClick={() => handleRemoveItem(item.id)}
                                            disabled={items.length === 1}
                                            size="small"
                                        >
                                            <RemoveCircleOutline fontSize="small" />
                                        </IconButton>
                                        {index === items.length - 1 && (
                                            <IconButton
                                                color="primary"
                                                onClick={handleAddItem}
                                                size="small"
                                                sx={{ ml: 0.5 }}
                                            >
                                                <AddCircleOutline fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Stack>

                    {/* Total + remaining balance */}
                    <Box
                        sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                px: 2.5,
                                py: 1.5,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #003049, #045182)',
                                color: '#fff',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                gap: { xs: 1, sm: 3 },
                                boxShadow: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 1.2,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.6,
                                        opacity: 0.9,
                                    }}
                                >
                                    Total Expense
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    ₹ {formatAmount(totalAmount)}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 1.2,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.6,
                                        opacity: 0.9,
                                    }}
                                >
                                    Balance After Entry
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        color:
                                            remainingBalance < 0 ? '#ffcccb' : '#ffffff',
                                    }}
                                >
                                    ₹ {formatAmount(remainingBalance)}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                            Upload Invoice / Bill
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 1.5, display: 'block' }}
                        >
                            Attach a photo or PDF copy of the bill.
                        </Typography>
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                            }}
                        >
                            {invoiceFile ? invoiceFile.name : 'Choose File'}
                            <input
                                type="file"
                                hidden
                                accept="image/*,application/pdf"
                                onChange={handleFileChange}
                            />
                        </Button>
                        {invoiceFile && (
                            <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={() => setInvoiceFile(null)}
                                sx={{ textTransform: 'none' }}
                            >
                                Remove
                            </Button>
                        )}
                    </Box>

                    {/* Actions */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            mt: 1,
                        }}
                    >
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={handleReset}
                            sx={{
                                textTransform: 'none',
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disableElevation
                            disabled={isSaveDisabled}
                            sx={{
                                px: 5,
                                py: 1.1,
                                borderRadius: 999,
                                fontWeight: 'bold',
                                textTransform: 'none',
                                backgroundColor: '#003049',
                                '&:hover': {
                                    backgroundColor: '#002433',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#c5c9cc',
                                    color: '#ffffff',
                                },
                            }}
                        >
                            Save Petty Cash Entry
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default PettyCash;
